// 浏览器环境组合式快捷键
// 该方法只适用浏览器环境限定范围的需求， electron桌面端全局要用请正常使用 globalShortcut

// 类型定义
type KeyCode = number

interface KeyMap {
  [key: string]: KeyCode
}

interface CombinationKeyOptions {
  /** 是否持续触发（按住时连续触发） */
  continuous?: boolean
  /** 是否在可编辑元素中禁用快捷键 */
  disableInEditable?: boolean
}

interface KeyHandler {
  pressed: Set<KeyCode>
  lastTriggered: boolean
  continuous: boolean
  callback?: (e: KeyboardEvent) => void
  sortedKeys?: KeyCode[]
  disableInEditable?: boolean
}

// 键码映射表
const KEY_MAP: KeyMap = {
  // 修饰键
  ...(['ctrl', 'control'] as const).reduce((o, k) => ({ ...o, [k]: 17 }), {}),
  ...(['shift'] as const).reduce((o, k) => ({ ...o, [k]: 16 }), {}),
  ...(['alt', 'option'] as const).reduce((o, k) => ({ ...o, [k]: 18 }), {}),
  ...(['meta', 'cmd', 'command', 'windows'] as const).reduce((o, k) => ({ ...o, [k]: 91 }), {}),

  // 功能键
  esc: 27,
  enter: 13,
  space: 32,
  tab: 9,
  capslock: 20,
  backspace: 8,
  delete: 46,
  del: 46,
  insert: 45,
  ins: 45,
  numlock: 144,
  scrolllock: 145,
  printscreen: 44,
  pause: 19,
  contextmenu: 93,

  // 导航键
  arrowup: 38,
  arrowdown: 40,
  arrowleft: 37,
  arrowright: 39,
  pageup: 33,
  pagedown: 34,
  home: 36,
  end: 35,

  // 数字键盘
  numpadmultiply: 106,
  numpadadd: 107,
  numpadsubtract: 109,
  numpaddecimal: 110,
  numpaddivide: 111,

  // 符号键
  '`': 192,
  '-': 189,
  '=': 187,
  '[': 219,
  ']': 221,
  '\\': 220,
  ';': 186,
  "'": 222,
  ',': 188,
  '.': 190,
  '/': 191,
}

// 字母键（a-z）
for (let i = 0; i < 26; i++) {
  const char = String.fromCharCode(97 + i)
  KEY_MAP[char] = 65 + i
}

// 数字键（0-9）
for (let i = 0; i <= 9; i++) {
  KEY_MAP[`${i}`] = 48 + i
}

// 数字键盘（0-9）
for (let i = 0; i <= 9; i++) {
  KEY_MAP[`numpad${i}`] = 96 + i
}

// 功能键扩展（F1-F24）
for (let i = 1; i <= 24; i++) {
  KEY_MAP[`f${i}`] = 111 + i
}

// 有效修饰键列表
const MODIFIER_KEYS = new Set<KeyCode>([
  16, // shift
  17, // ctrl
  18, // alt
  91, // meta
  9, // tab
  46, // delete
  38, // arrowup
  40, // arrowdown
  37, // arrowleft
  39, // arrowright
  13, // enter
  ...Array.from({ length: 12 }, (_, i) => 112 + i), // F1-F12
])

/**
 * 快捷键事件管理器
 */
class ShortcutManager {
  private handlers: Map<string, KeyHandler> = new Map()
  private isListening: boolean = false

  // 绑定的事件处理函数（保持引用以便移除）
  private boundHandleKeydown: (e: KeyboardEvent) => void
  private boundHandleKeyup: (e: KeyboardEvent) => void
  private boundHandleFocus: () => void

  constructor() {
    this.boundHandleKeydown = this.handleKeydown.bind(this)
    this.boundHandleKeyup = this.handleKeyup.bind(this)
    this.boundHandleFocus = this.handleFocus.bind(this)
  }

  /**
   * 注册快捷键
   */
  register(
    combo: string,
    callback: (e: KeyboardEvent) => void,
    options: CombinationKeyOptions = {},
  ): () => void {
    const keys = this.parseCombo(combo)
    const sortedKeys = [...keys].sort()
    const comboKey = this.getComboKey(sortedKeys)

    if (this.handlers.has(comboKey)) {
      console.warn(`快捷键 ${combo} 已被注册，将覆盖原有处理函数`)
    }

    this.handlers.set(comboKey, {
      pressed: new Set(),
      lastTriggered: false,
      continuous: options.continuous ?? false,
      callback,
      sortedKeys,
      disableInEditable: options.disableInEditable ?? true,
    })

    // 返回注销函数
    return () => {
      this.unregister(combo)
    }
  }

  /**
   * 注销快捷键
   */
  unregister(combo: string): void {
    try {
      const keys = this.parseCombo(combo)
      const sortedKeys = [...keys].sort()
      const comboKey = this.getComboKey(sortedKeys)
      this.handlers.delete(comboKey)
    } catch (error) {
      // 解析失败，忽略注销
      console.warn(`Failed to unregister shortcut: ${combo}`, error)
    }
  }

  /**
   * 注销所有快捷键
   */
  unregisterAll(): void {
    this.handlers.clear()
  }

  /**
   * 开始监听
   */
  startListening(): void {
    if (this.isListening) return

    window.addEventListener('keydown', this.boundHandleKeydown, true)
    window.addEventListener('keyup', this.boundHandleKeyup, true)
    window.addEventListener('focus', this.boundHandleFocus)
    this.isListening = true
  }

  /**
   * 停止监听
   */
  stopListening(): void {
    if (!this.isListening) return

    window.removeEventListener('keydown', this.boundHandleKeydown, true)
    window.removeEventListener('keyup', this.boundHandleKeyup, true)
    window.removeEventListener('focus', this.boundHandleFocus)
    this.isListening = false
  }

  /**
   * 检查是否正在监听
   */
  isActive(): boolean {
    return this.isListening
  }

  /**
   * 重置所有快捷键的状态
   */
  resetAll(): void {
    this.handlers.forEach((handler) => {
      handler.pressed.clear()
      handler.lastTriggered = false
    })
  }

  /**
   * 键盘按下事件处理
   */
  private handleKeydown(e: KeyboardEvent): void {
    const target = e.target as HTMLElement

    this.handlers.forEach((handler) => {
      // 检查是否在可编辑元素中
      if (handler.disableInEditable && this.isEditableElement(target)) {
        return
      }

      const keyCode = e.keyCode

      if (handler.pressed.has(keyCode) && !handler.continuous) {
        return
      }

      handler.pressed.add(keyCode)
      this.checkCombination(e, handler)
    })
  }

  /**
   * 键盘松开事件处理
   */
  private handleKeyup(e: KeyboardEvent): void {
    const keyCode = e.keyCode

    this.handlers.forEach((handler) => {
      handler.pressed.delete(keyCode)

      if (!handler.continuous) {
        handler.lastTriggered = false
      }
    })
  }

  /**
   * 窗口聚焦时重置所有状态
   */
  private handleFocus(): void {
    this.resetAll()
  }

  /**
   * 组合键检查
   */
  private checkCombination(e: KeyboardEvent, handler: KeyHandler): void {
    if (!handler.sortedKeys) return

    const currentKeys = Array.from(handler.pressed).sort()

    const isExactMatch =
      currentKeys.length === handler.sortedKeys.length &&
      currentKeys.every((key, index) => key === handler.sortedKeys![index])

    if (isExactMatch) {
      if (handler.continuous || !handler.lastTriggered) {
        if (handler.callback) {
          handler.callback(e)
        }

        if (!handler.continuous) {
          handler.lastTriggered = true
        }
      }
    }
  }

  /**
   * 判断是否为可编辑元素
   */
  private isEditableElement(element: HTMLElement): boolean {
    const tagName = element.tagName.toUpperCase()

    if (tagName === 'INPUT') {
      const input = element as HTMLInputElement
      const inputTypes = ['text', 'search', 'password', 'email', 'number', 'tel', 'url']
      return inputTypes.includes(input.type.toLowerCase())
    }

    if (tagName === 'TEXTAREA') return true

    return element.isContentEditable === true
  }

  /**
   * 解析组合键字符串
   */
  private parseCombo(combo: string): KeyCode[] {
    const comboArr = combo
      .toLowerCase()
      .split('+')
      .map((key) => {
        const parsedKey = key.trim()
        const code = KEY_MAP[parsedKey]

        if (code === undefined) {
          throw new Error(`Invalid key: ${parsedKey}`)
        }

        return code
      })

    if (comboArr.some((k) => MODIFIER_KEYS.has(k))) {
      return comboArr
    }

    throw new Error(`Invalid combination: ${combo} - must include at least one modifier key`)
  }

  /**
   * 获取组合键的唯一标识
   */
  private getComboKey(sortedKeys: KeyCode[]): string {
    return sortedKeys.join('+')
  }
}

// 全局单例管理器
let globalManager: ShortcutManager | null = null

/**
 * 获取全局快捷键管理器
 */
export function getGlobalShortcutManager(): ShortcutManager {
  if (!globalManager) {
    globalManager = new ShortcutManager()
  }
  return globalManager
}

/**
 * Vue Composable: 使用组合键
 */
export function useCombinationKey(
  combo: string,
  callback: (e: KeyboardEvent) => void,
  options: CombinationKeyOptions | boolean = {},
): {
  register: () => void
  unregister: () => void
  isActive: () => boolean
} {
  // 兼容旧的布尔值参数
  let normalizedOptions: CombinationKeyOptions
  if (typeof options === 'boolean') {
    normalizedOptions = { continuous: options }
  } else if (options && typeof options === 'object') {
    normalizedOptions = options
  } else {
    normalizedOptions = {}
  }

  const manager = getGlobalShortcutManager()
  let unregisterFn: (() => void) | null = null

  const register = () => {
    if (unregisterFn) {
      unregisterFn()
    }
    unregisterFn = manager.register(combo, callback, normalizedOptions)
  }

  const unregister = () => {
    if (unregisterFn) {
      unregisterFn()
      unregisterFn = null
    }
  }

  const isActive = () => {
    return manager.isActive()
  }

  register()
  // 确保管理器开始监听
  if (!manager.isActive()) {
    manager.startListening()
  }

  return {
    register,
    unregister,
    isActive,
  }
}
